import React, { useState, useEffect } from 'react';
import HoverInfo from './HoverInfo';
import TableCell from './TableCell';
import config from '../config';

  const { apiBaseURL } = config;

  // Helper function to construct API URLs
  export const getApiUrl = (endpoint) => {
    return `${apiBaseURL}${endpoint}`;
  };

  const FinancialData = ({ data }) => {
  const [activeFinancialTab, setActiveFinancialTab] = useState('income');
  const [period, setPeriod] = useState('annual');
  const [financialData, setFinancialData] = useState(data);
  const [hoverInfo, setHoverInfo] = useState({ position: null, content: null });
  
  // Effect to refetch data when period changes
  useEffect(() => {
    const fetchDataForPeriod = async () => {
      if (!data || !data.stock) return;
      
      const symbol = data.stock.symbol;
      console.log(`Fetching ${period} financial data for ${symbol}`);
      
      try {
        // Fetch financial data for the selected period
        const [incomeResponse, balanceResponse, cashflowResponse, ratiosResponse] = await Promise.all([
          fetch(`${apiBaseURL}/api/financials/income/${symbol}?period=${period}`),
          fetch(`${apiBaseURL}/api/financials/balance/${symbol}?period=${period}`),
          fetch(`${apiBaseURL}/api/financials/cashflow/${symbol}?period=${period}`),
          fetch(`${apiBaseURL}/api/financials/ratios/${symbol}?period=${period}`)
        ]);
        
        // Process responses
        const income = incomeResponse.ok ? await incomeResponse.json() : data.income;
        const balance = balanceResponse.ok ? await balanceResponse.json() : data.balance;
        const cashflow = cashflowResponse.ok ? await cashflowResponse.json() : data.cashflow;
        const ratios = ratiosResponse.ok ? await ratiosResponse.json() : data.ratios;
        
        // Update component state with new data
        setFinancialData({
          ...data,
          income,
          balance,
          cashflow,
          ratios
        });
        
        console.log(`${period} financial data fetched successfully`);
      } catch (error) {
        console.error(`Error fetching ${period} financial data:`, error);
      }
    };
    
    fetchDataForPeriod();
  }, [period, data, data.stock]);
  
  // Set up handlers for hover info
  const handleCellHover = (position, content) => {
    setHoverInfo({ position, content });
  };
  
  const handleCellLeave = () => {
    setHoverInfo({ position: null, content: null });
  };
  
  // Helper function to ensure a value is a number
  const ensureNumber = (value, defaultValue = 0) => {
    if (value === null || value === undefined || value === '') return defaultValue;
    const num = typeof value === 'number' ? value : parseFloat(value);
    return isNaN(num) ? defaultValue : num;
  };
  
  // Helper function to safely format a number with fixed decimal places
  const safeFormat = (value, formatter = (x) => x, defaultValue = 'N/A') => {
    try {
      if (value === null || value === undefined || value === '') return defaultValue;
      return formatter(value);
    } catch (e) {
      console.error("Error formatting value:", value, e);
      return defaultValue;
    }
  };
  
  // Safe version of toFixed
  const safeToFixed = (value, decimals = 2) => {
    return safeFormat(value, (val) => {
      const num = typeof val === 'number' ? val : parseFloat(val);
      return isNaN(num) ? '0.00' : num.toFixed(decimals);
    }, '0.00');
  };
  
  // Format large numbers for better readability
  const formatNumber = (value) => {
    if (value === null || value === undefined) return 'N/A';
    
    // Ensure we're working with a number
    const num = ensureNumber(value);
    
    // For numbers less than 1 million
    if (Math.abs(num) < 1000000) {
      return new Intl.NumberFormat('en-US').format(num);
    }
    
    // For millions
    if (Math.abs(num) < 1000000000) {
      return new Intl.NumberFormat('en-US', { 
        maximumFractionDigits: 2 
      }).format(num / 1000000) + 'M';
    }
    
    // For billions
    return new Intl.NumberFormat('en-US', { 
      maximumFractionDigits: 2 
    }).format(num / 1000000000) + 'B';
  };
  
  // Format date
  const formatDate = (dateString) => {
    try {
      if (!dateString) return 'N/A';
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'Invalid Date';
      
      // Format based on period
      if (period === 'quarter') {
        return `Q${Math.floor(date.getMonth() / 3) + 1} ${date.getFullYear()}`;
      } else {
        return date.getFullYear().toString();
      }
    } catch (error) {
      console.error("Error formatting date:", dateString, error);
      return 'N/A';
    }
  };

  // Render income statements
  const renderIncomeStatements = () => {
    if (!financialData.income || !financialData.income.incomeStatements || financialData.income.incomeStatements.length === 0) {
      return <div>No income statement data available</div>;
    }
    
    const statements = financialData.income.incomeStatements.slice().sort((a, b) => new Date(b.date) - new Date(a.date));
    console.log(`Rendering ${statements.length} ${period} income statements`);
    
    return (
      <div className="financial-table-container">
        <table className="financial-table">
          <thead>
            <tr>
              <th>Item</th>
              {statements.map((statement, index) => (
                <th key={index}>{formatDate(statement.date)}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Revenue</td>
              {statements.map((statement, index) => {
                const previousValue = index < statements.length - 1 ? statements[index + 1].revenue : null;
                return (
                  <TableCell 
                    key={index}
                    value={statement.revenue}
                    previousValue={previousValue}
                    formatter={formatNumber}
                    rowName="Revenue"
                    onHover={handleCellHover}
                    onLeave={handleCellLeave}
                  />
                );
              })}
            </tr>
            <tr>
              <td>Cost of Revenue</td>
              {statements.map((statement, index) => {
                const previousValue = index < statements.length - 1 ? statements[index + 1].cost_of_revenue : null;
                return (
                  <TableCell 
                    key={index}
                    value={statement.cost_of_revenue}
                    previousValue={previousValue}
                    formatter={formatNumber}
                    rowName="Cost of Revenue"
                    onHover={handleCellHover}
                    onLeave={handleCellLeave}
                  />
                );
              })}
            </tr>
            <tr className="highlighted">
              <td>Gross Profit</td>
              {statements.map((statement, index) => {
                const previousValue = index < statements.length - 1 ? statements[index + 1].gross_profit : null;
                return (
                  <TableCell 
                    key={index}
                    value={statement.gross_profit}
                    previousValue={previousValue}
                    formatter={formatNumber}
                    rowName="Gross Profit"
                    onHover={handleCellHover}
                    onLeave={handleCellLeave}
                  />
                );
              })}
            </tr>
            <tr>
              <td>Operating Expenses</td>
              {statements.map((statement, index) => {
                const previousValue = index < statements.length - 1 ? statements[index + 1].operating_expenses : null;
                return (
                  <TableCell 
                    key={index}
                    value={statement.operating_expenses}
                    previousValue={previousValue}
                    formatter={formatNumber}
                    rowName="Operating Expenses"
                    onHover={handleCellHover}
                    onLeave={handleCellLeave}
                  />
                );
              })}
            </tr>
            <tr className="highlighted">
              <td>Operating Income</td>
              {statements.map((statement, index) => {
                const previousValue = index < statements.length - 1 ? statements[index + 1].operating_income : null;
                return (
                  <TableCell 
                    key={index}
                    value={statement.operating_income}
                    previousValue={previousValue}
                    formatter={formatNumber}
                    rowName="Operating Income"
                    onHover={handleCellHover}
                    onLeave={handleCellLeave}
                  />
                );
              })}
            </tr>
            <tr>
              <td>Income Before Tax</td>
              {statements.map((statement, index) => {
                const previousValue = index < statements.length - 1 ? statements[index + 1].income_before_tax : null;
                return (
                  <TableCell 
                    key={index}
                    value={statement.income_before_tax}
                    previousValue={previousValue}
                    formatter={formatNumber}
                    rowName="Income Before Tax"
                    onHover={handleCellHover}
                    onLeave={handleCellLeave}
                  />
                );
              })}
            </tr>
            <tr>
              <td>Income Tax Expense</td>
              {statements.map((statement, index) => {
                const previousValue = index < statements.length - 1 ? statements[index + 1].income_tax_expense : null;
                return (
                  <TableCell 
                    key={index}
                    value={statement.income_tax_expense}
                    previousValue={previousValue}
                    formatter={formatNumber}
                    rowName="Income Tax Expense"
                    onHover={handleCellHover}
                    onLeave={handleCellLeave}
                  />
                );
              })}
            </tr>
            <tr className="highlighted">
              <td>Net Income</td>
              {statements.map((statement, index) => {
                const previousValue = index < statements.length - 1 ? statements[index + 1].net_income : null;
                return (
                  <TableCell 
                    key={index}
                    value={statement.net_income}
                    previousValue={previousValue}
                    formatter={formatNumber}
                    rowName="Net Income"
                    onHover={handleCellHover}
                    onLeave={handleCellLeave}
                  />
                );
              })}
            </tr>
            <tr>
              <td>EPS</td>
              {statements.map((statement, index) => {
                const previousValue = index < statements.length - 1 ? statements[index + 1].eps : null;
                return (
                  <TableCell 
                    key={index}
                    value={statement.eps}
                    previousValue={previousValue}
                    formatter={(v) => `$${safeToFixed(v)}`}
                    rowName="Earnings Per Share"
                    onHover={handleCellHover}
                    onLeave={handleCellLeave}
                  />
                );
              })}
            </tr>
            <tr>
              <td>EPS (Diluted)</td>
              {statements.map((statement, index) => {
                const previousValue = index < statements.length - 1 ? statements[index + 1].eps_diluted : null;
                return (
                  <TableCell 
                    key={index}
                    value={statement.eps_diluted}
                    previousValue={previousValue}
                    formatter={(v) => `$${safeToFixed(v)}`}
                    rowName="EPS (Diluted)"
                    onHover={handleCellHover}
                    onLeave={handleCellLeave}
                  />
                );
              })}
            </tr>
          </tbody>
        </table>
      </div>
    );
  };
  
  // Render balance sheets
  // This code snippet should replace the renderBalanceSheets function in your FinancialData.js file

  // Render balance sheets
  const renderBalanceSheets = () => {
    if (!financialData.balance || !financialData.balance.balanceSheets || financialData.balance.balanceSheets.length === 0) {
      return <div>No balance sheet data available</div>;
    }
    
    const balanceSheets = financialData.balance.balanceSheets.slice().sort((a, b) => new Date(b.date) - new Date(a.date));
    
    return (
      <div className="financial-table-container">
        <table className="financial-table">
          <thead>
            <tr>
              <th>Item</th>
              {balanceSheets.map((sheet, index) => (
                <th key={index}>{formatDate(sheet.date)}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {/* Assets Section */}
            <tr className="highlighted">
              <td colSpan={balanceSheets.length + 1} style={{ textAlign: 'left', fontWeight: 'bold', backgroundColor: 'rgba(52, 152, 219, 0.1)' }}>
                Assets
              </td>
            </tr>
            <tr>
              <td>Cash & Equivalents</td>
              {balanceSheets.map((sheet, index) => {
                const previousValue = index < balanceSheets.length - 1 ? balanceSheets[index + 1].cash_and_equivalents : null;
                return (
                  <TableCell 
                    key={index}
                    value={sheet.cash_and_equivalents}
                    previousValue={previousValue}
                    formatter={formatNumber}
                    rowName="Cash & Equivalents"
                    onHover={handleCellHover}
                    onLeave={handleCellLeave}
                  />
                );
              })}
            </tr>
            <tr>
              <td>Short-Term Investments</td>
              {balanceSheets.map((sheet, index) => {
                const previousValue = index < balanceSheets.length - 1 ? balanceSheets[index + 1].short_term_investments : null;
                return (
                  <TableCell 
                    key={index}
                    value={sheet.short_term_investments}
                    previousValue={previousValue}
                    formatter={formatNumber}
                    rowName="Short-Term Investments"
                    onHover={handleCellHover}
                    onLeave={handleCellLeave}
                  />
                );
              })}
            </tr>
            <tr>
              <td>Accounts Receivable</td>
              {balanceSheets.map((sheet, index) => {
                const previousValue = index < balanceSheets.length - 1 ? balanceSheets[index + 1].accounts_receivable : null;
                return (
                  <TableCell 
                    key={index}
                    value={sheet.accounts_receivable}
                    previousValue={previousValue}
                    formatter={formatNumber}
                    rowName="Accounts Receivable"
                    onHover={handleCellHover}
                    onLeave={handleCellLeave}
                  />
                );
              })}
            </tr>
            <tr>
              <td>Inventory</td>
              {balanceSheets.map((sheet, index) => {
                const previousValue = index < balanceSheets.length - 1 ? balanceSheets[index + 1].inventory : null;
                return (
                  <TableCell 
                    key={index}
                    value={sheet.inventory}
                    previousValue={previousValue}
                    formatter={formatNumber}
                    rowName="Inventory"
                    onHover={handleCellHover}
                    onLeave={handleCellLeave}
                  />
                );
              })}
            </tr>
            <tr>
              <td>Other Current Assets</td>
              {balanceSheets.map((sheet, index) => {
                const previousValue = index < balanceSheets.length - 1 ? balanceSheets[index + 1].other_current_assets : null;
                return (
                  <TableCell 
                    key={index}
                    value={sheet.other_current_assets}
                    previousValue={previousValue}
                    formatter={formatNumber}
                    rowName="Other Current Assets"
                    onHover={handleCellHover}
                    onLeave={handleCellLeave}
                  />
                );
              })}
            </tr>
            <tr className="highlighted">
              <td>Total Current Assets</td>
              {balanceSheets.map((sheet, index) => {
                const previousValue = index < balanceSheets.length - 1 ? balanceSheets[index + 1].total_current_assets : null;
                return (
                  <TableCell 
                    key={index}
                    value={sheet.total_current_assets}
                    previousValue={previousValue}
                    formatter={formatNumber}
                    rowName="Total Current Assets"
                    onHover={handleCellHover}
                    onLeave={handleCellLeave}
                  />
                );
              })}
            </tr>
            <tr>
              <td>Property, Plant & Equipment</td>
              {balanceSheets.map((sheet, index) => {
                const previousValue = index < balanceSheets.length - 1 ? balanceSheets[index + 1].property_plant_equipment : null;
                return (
                  <TableCell 
                    key={index}
                    value={sheet.property_plant_equipment}
                    previousValue={previousValue}
                    formatter={formatNumber}
                    rowName="Property, Plant & Equipment"
                    onHover={handleCellHover}
                    onLeave={handleCellLeave}
                  />
                );
              })}
            </tr>
            <tr>
              <td>Long-Term Investments</td>
              {balanceSheets.map((sheet, index) => {
                const previousValue = index < balanceSheets.length - 1 ? balanceSheets[index + 1].long_term_investments : null;
                return (
                  <TableCell 
                    key={index}
                    value={sheet.long_term_investments}
                    previousValue={previousValue}
                    formatter={formatNumber}
                    rowName="Long-Term Investments"
                    onHover={handleCellHover}
                    onLeave={handleCellLeave}
                  />
                );
              })}
            </tr>
            <tr>
              <td>Goodwill</td>
              {balanceSheets.map((sheet, index) => {
                const previousValue = index < balanceSheets.length - 1 ? balanceSheets[index + 1].goodwill : null;
                return (
                  <TableCell 
                    key={index}
                    value={sheet.goodwill}
                    previousValue={previousValue}
                    formatter={formatNumber}
                    rowName="Goodwill"
                    onHover={handleCellHover}
                    onLeave={handleCellLeave}
                  />
                );
              })}
            </tr>
            <tr>
              <td>Intangible Assets</td>
              {balanceSheets.map((sheet, index) => {
                const previousValue = index < balanceSheets.length - 1 ? balanceSheets[index + 1].intangible_assets : null;
                return (
                  <TableCell 
                    key={index}
                    value={sheet.intangible_assets}
                    previousValue={previousValue}
                    formatter={formatNumber}
                    rowName="Intangible Assets"
                    onHover={handleCellHover}
                    onLeave={handleCellLeave}
                  />
                );
              })}
            </tr>
            <tr>
              <td>Other Non-Current Assets</td>
              {balanceSheets.map((sheet, index) => {
                const previousValue = index < balanceSheets.length - 1 ? balanceSheets[index + 1].other_non_current_assets : null;
                return (
                  <TableCell 
                    key={index}
                    value={sheet.other_non_current_assets}
                    previousValue={previousValue}
                    formatter={formatNumber}
                    rowName="Other Non-Current Assets"
                    onHover={handleCellHover}
                    onLeave={handleCellLeave}
                  />
                );
              })}
            </tr>
            <tr className="highlighted">
              <td>Total Assets</td>
              {balanceSheets.map((sheet, index) => {
                const previousValue = index < balanceSheets.length - 1 ? balanceSheets[index + 1].total_assets : null;
                return (
                  <TableCell 
                    key={index}
                    value={sheet.total_assets}
                    previousValue={previousValue}
                    formatter={formatNumber}
                    rowName="Total Assets"
                    onHover={handleCellHover}
                    onLeave={handleCellLeave}
                  />
                );
              })}
            </tr>
            
            {/* Liabilities Section */}
            <tr className="highlighted">
              <td colSpan={balanceSheets.length + 1} style={{ textAlign: 'left', fontWeight: 'bold', backgroundColor: 'rgba(52, 152, 219, 0.1)' }}>
                Liabilities
              </td>
            </tr>
            <tr>
              <td>Accounts Payable</td>
              {balanceSheets.map((sheet, index) => {
                const previousValue = index < balanceSheets.length - 1 ? balanceSheets[index + 1].accounts_payable : null;
                return (
                  <TableCell 
                    key={index}
                    value={sheet.accounts_payable}
                    previousValue={previousValue}
                    formatter={formatNumber}
                    rowName="Accounts Payable"
                    onHover={handleCellHover}
                    onLeave={handleCellLeave}
                  />
                );
              })}
            </tr>
            <tr>
              <td>Short-Term Debt</td>
              {balanceSheets.map((sheet, index) => {
                const previousValue = index < balanceSheets.length - 1 ? balanceSheets[index + 1].short_term_debt : null;
                return (
                  <TableCell 
                    key={index}
                    value={sheet.short_term_debt}
                    previousValue={previousValue}
                    formatter={formatNumber}
                    rowName="Short-Term Debt"
                    onHover={handleCellHover}
                    onLeave={handleCellLeave}
                  />
                );
              })}
            </tr>
            <tr>
              <td>Current Portion of Long-Term Debt</td>
              {balanceSheets.map((sheet, index) => {
                const previousValue = index < balanceSheets.length - 1 ? balanceSheets[index + 1].current_portion_long_term_debt : null;
                return (
                  <TableCell 
                    key={index}
                    value={sheet.current_portion_long_term_debt}
                    previousValue={previousValue}
                    formatter={formatNumber}
                    rowName="Current Portion of Long-Term Debt"
                    onHover={handleCellHover}
                    onLeave={handleCellLeave}
                  />
                );
              })}
            </tr>
            <tr>
              <td>Other Current Liabilities</td>
              {balanceSheets.map((sheet, index) => {
                const previousValue = index < balanceSheets.length - 1 ? balanceSheets[index + 1].other_current_liabilities : null;
                return (
                  <TableCell 
                    key={index}
                    value={sheet.other_current_liabilities}
                    previousValue={previousValue}
                    formatter={formatNumber}
                    rowName="Other Current Liabilities"
                    onHover={handleCellHover}
                    onLeave={handleCellLeave}
                  />
                );
              })}
            </tr>
            <tr className="highlighted">
              <td>Total Current Liabilities</td>
              {balanceSheets.map((sheet, index) => {
                const previousValue = index < balanceSheets.length - 1 ? balanceSheets[index + 1].total_current_liabilities : null;
                return (
                  <TableCell 
                    key={index}
                    value={sheet.total_current_liabilities}
                    previousValue={previousValue}
                    formatter={formatNumber}
                    rowName="Total Current Liabilities"
                    onHover={handleCellHover}
                    onLeave={handleCellLeave}
                  />
                );
              })}
            </tr>
            <tr>
              <td>Long-Term Debt</td>
              {balanceSheets.map((sheet, index) => {
                const previousValue = index < balanceSheets.length - 1 ? balanceSheets[index + 1].long_term_debt : null;
                return (
                  <TableCell 
                    key={index}
                    value={sheet.long_term_debt}
                    previousValue={previousValue}
                    formatter={formatNumber}
                    rowName="Long-Term Debt"
                    onHover={handleCellHover}
                    onLeave={handleCellLeave}
                  />
                );
              })}
            </tr>
            <tr>
              <td>Deferred Tax Liabilities</td>
              {balanceSheets.map((sheet, index) => {
                const previousValue = index < balanceSheets.length - 1 ? balanceSheets[index + 1].deferred_tax_liabilities : null;
                return (
                  <TableCell 
                    key={index}
                    value={sheet.deferred_tax_liabilities}
                    previousValue={previousValue}
                    formatter={formatNumber}
                    rowName="Deferred Tax Liabilities"
                    onHover={handleCellHover}
                    onLeave={handleCellLeave}
                  />
                );
              })}
            </tr>
            <tr>
              <td>Other Non-Current Liabilities</td>
              {balanceSheets.map((sheet, index) => {
                const previousValue = index < balanceSheets.length - 1 ? balanceSheets[index + 1].other_non_current_liabilities : null;
                return (
                  <TableCell 
                    key={index}
                    value={sheet.other_non_current_liabilities}
                    previousValue={previousValue}
                    formatter={formatNumber}
                    rowName="Other Non-Current Liabilities"
                    onHover={handleCellHover}
                    onLeave={handleCellLeave}
                  />
                );
              })}
            </tr>
            <tr className="highlighted">
              <td>Total Liabilities</td>
              {balanceSheets.map((sheet, index) => {
                const previousValue = index < balanceSheets.length - 1 ? balanceSheets[index + 1].total_liabilities : null;
                return (
                  <TableCell 
                    key={index}
                    value={sheet.total_liabilities}
                    previousValue={previousValue}
                    formatter={formatNumber}
                    rowName="Total Liabilities"
                    onHover={handleCellHover}
                    onLeave={handleCellLeave}
                  />
                );
              })}
            </tr>
            
            {/* Shareholders' Equity Section */}
            <tr className="highlighted">
              <td colSpan={balanceSheets.length + 1} style={{ textAlign: 'left', fontWeight: 'bold', backgroundColor: 'rgba(52, 152, 219, 0.1)' }}>
                Shareholders' Equity
              </td>
            </tr>
            <tr>
              <td>Common Stock</td>
              {balanceSheets.map((sheet, index) => {
                const previousValue = index < balanceSheets.length - 1 ? balanceSheets[index + 1].common_stock : null;
                return (
                  <TableCell 
                    key={index}
                    value={sheet.common_stock}
                    previousValue={previousValue}
                    formatter={formatNumber}
                    rowName="Common Stock"
                    onHover={handleCellHover}
                    onLeave={handleCellLeave}
                  />
                );
              })}
            </tr>
            <tr>
              <td>Retained Earnings</td>
              {balanceSheets.map((sheet, index) => {
                const previousValue = index < balanceSheets.length - 1 ? balanceSheets[index + 1].retained_earnings : null;
                return (
                  <TableCell 
                    key={index}
                    value={sheet.retained_earnings}
                    previousValue={previousValue}
                    formatter={formatNumber}
                    rowName="Retained Earnings"
                    onHover={handleCellHover}
                    onLeave={handleCellLeave}
                  />
                );
              })}
            </tr>
            <tr>
              <td>Treasury Stock</td>
              {balanceSheets.map((sheet, index) => {
                const previousValue = index < balanceSheets.length - 1 ? balanceSheets[index + 1].treasury_stock : null;
                return (
                  <TableCell 
                    key={index}
                    value={sheet.treasury_stock}
                    previousValue={previousValue}
                    formatter={formatNumber}
                    rowName="Treasury Stock"
                    onHover={handleCellHover}
                    onLeave={handleCellLeave}
                  />
                );
              })}
            </tr>
            <tr>
              <td>Accumulated Other Comprehensive Income</td>
              {balanceSheets.map((sheet, index) => {
                const previousValue = index < balanceSheets.length - 1 ? balanceSheets[index + 1].accumulated_other_comprehensive_income : null;
                return (
                  <TableCell 
                    key={index}
                    value={sheet.accumulated_other_comprehensive_income}
                    previousValue={previousValue}
                    formatter={formatNumber}
                    rowName="Accumulated Other Comprehensive Income"
                    onHover={handleCellHover}
                    onLeave={handleCellLeave}
                  />
                );
              })}
            </tr>
            <tr className="highlighted">
              <td>Total Stockholders' Equity</td>
              {balanceSheets.map((sheet, index) => {
                const previousValue = index < balanceSheets.length - 1 ? balanceSheets[index + 1].total_stockholders_equity : null;
                return (
                  <TableCell 
                    key={index}
                    value={sheet.total_stockholders_equity}
                    previousValue={previousValue}
                    formatter={formatNumber}
                    rowName="Total Stockholders' Equity"
                    onHover={handleCellHover}
                    onLeave={handleCellLeave}
                  />
                );
              })}
            </tr>
            <tr className="highlighted">
              <td>Total Liabilities and Stockholders' Equity</td>
              {balanceSheets.map((sheet, index) => {
                const previousValue = index < balanceSheets.length - 1 ? balanceSheets[index + 1].total_liabilities_and_equity : null;
                return (
                  <TableCell 
                    key={index}
                    value={sheet.total_liabilities_and_equity || sheet.total_assets}
                    previousValue={previousValue}
                    formatter={formatNumber}
                    rowName="Total Liabilities and Stockholders' Equity"
                    onHover={handleCellHover}
                    onLeave={handleCellLeave}
                  />
                );
              })}
            </tr>
          </tbody>
        </table>
      </div>
    );
  };

  // Render cash flow statements
const renderCashFlows = () => {
  if (!financialData.cashflow || !financialData.cashflow.cashFlowStatements || financialData.cashflow.cashFlowStatements.length === 0) {
    return <div>No cash flow data available</div>;
  }
  
  const cashFlows = financialData.cashflow.cashFlowStatements.slice().sort((a, b) => new Date(b.date) - new Date(a.date));
  
  return (
    <div className="financial-table-container">
      <table className="financial-table">
        <thead>
          <tr>
            <th>Item</th>
            {cashFlows.map((flow, index) => (
              <th key={index}>{formatDate(flow.date)}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Net Income</td>
            {cashFlows.map((flow, index) => {
              const previousValue = index < cashFlows.length - 1 ? cashFlows[index + 1].net_income : null;
              return (
                <TableCell 
                  key={index}
                  value={flow.net_income}
                  previousValue={previousValue}
                  formatter={formatNumber}
                  rowName="Net Income"
                  onHover={handleCellHover}
                  onLeave={handleCellLeave}
                />
              );
            })}
          </tr>
          <tr>
            <td>Depreciation & Amortization</td>
            {cashFlows.map((flow, index) => {
              const previousValue = index < cashFlows.length - 1 ? cashFlows[index + 1].depreciation_and_amortization : null;
              return (
                <TableCell 
                  key={index}
                  value={flow.depreciation_and_amortization}
                  previousValue={previousValue}
                  formatter={formatNumber}
                  rowName="Depreciation & Amortization"
                  onHover={handleCellHover}
                  onLeave={handleCellLeave}
                />
              );
            })}
          </tr>
          <tr className="highlighted">
            <td>Cash from Operating Activities</td>
            {cashFlows.map((flow, index) => {
              const previousValue = index < cashFlows.length - 1 ? cashFlows[index + 1].cash_from_operating_activities : null;
              return (
                <TableCell 
                  key={index}
                  value={flow.cash_from_operating_activities}
                  previousValue={previousValue}
                  formatter={formatNumber}
                  rowName="Cash from Operating Activities"
                  onHover={handleCellHover}
                  onLeave={handleCellLeave}
                />
              );
            })}
          </tr>
          <tr>
            <td>Capital Expenditure</td>
            {cashFlows.map((flow, index) => {
              const previousValue = index < cashFlows.length - 1 ? cashFlows[index + 1].capital_expenditure : null;
              return (
                <TableCell 
                  key={index}
                  value={flow.capital_expenditure}
                  previousValue={previousValue}
                  formatter={formatNumber}
                  rowName="Capital Expenditure"
                  onHover={handleCellHover}
                  onLeave={handleCellLeave}
                />
              );
            })}
          </tr>
          <tr className="highlighted">
            <td>Cash from Investing Activities</td>
            {cashFlows.map((flow, index) => {
              const previousValue = index < cashFlows.length - 1 ? cashFlows[index + 1].cash_from_investing_activities : null;
              return (
                <TableCell 
                  key={index}
                  value={flow.cash_from_investing_activities}
                  previousValue={previousValue}
                  formatter={formatNumber}
                  rowName="Cash from Investing Activities"
                  onHover={handleCellHover}
                  onLeave={handleCellLeave}
                />
              );
            })}
          </tr>
          <tr>
            <td>Debt Repayment</td>
            {cashFlows.map((flow, index) => {
              const previousValue = index < cashFlows.length - 1 ? cashFlows[index + 1].debt_repayment : null;
              return (
                <TableCell 
                  key={index}
                  value={flow.debt_repayment}
                  previousValue={previousValue}
                  formatter={formatNumber}
                  rowName="Debt Repayment"
                  onHover={handleCellHover}
                  onLeave={handleCellLeave}
                />
              );
            })}
          </tr>
          <tr>
            <td>Stock Repurchase</td>
            {cashFlows.map((flow, index) => {
              const previousValue = index < cashFlows.length - 1 ? cashFlows[index + 1].stock_repurchase : null;
              return (
                <TableCell 
                  key={index}
                  value={flow.stock_repurchase}
                  previousValue={previousValue}
                  formatter={formatNumber}
                  rowName="Stock Repurchase"
                  onHover={handleCellHover}
                  onLeave={handleCellLeave}
                />
              );
            })}
          </tr>
          <tr>
            <td>Dividends Paid</td>
            {cashFlows.map((flow, index) => {
              const previousValue = index < cashFlows.length - 1 ? cashFlows[index + 1].dividends_paid : null;
              return (
                <TableCell 
                  key={index}
                  value={flow.dividends_paid}
                  previousValue={previousValue}
                  formatter={formatNumber}
                  rowName="Dividends Paid"
                  onHover={handleCellHover}
                  onLeave={handleCellLeave}
                />
              );
            })}
          </tr>
          <tr className="highlighted">
            <td>Cash from Financing Activities</td>
            {cashFlows.map((flow, index) => {
              const previousValue = index < cashFlows.length - 1 ? cashFlows[index + 1].cash_from_financing_activities : null;
              return (
                <TableCell 
                  key={index}
                  value={flow.cash_from_financing_activities}
                  previousValue={previousValue}
                  formatter={formatNumber}
                  rowName="Cash from Financing Activities"
                  onHover={handleCellHover}
                  onLeave={handleCellLeave}
                />
              );
            })}
          </tr>
          <tr className="highlighted">
            <td>Free Cash Flow</td>
            {cashFlows.map((flow, index) => {
              const previousValue = index < cashFlows.length - 1 ? cashFlows[index + 1].free_cash_flow : null;
              return (
                <TableCell 
                  key={index}
                  value={flow.free_cash_flow}
                  previousValue={previousValue}
                  formatter={formatNumber}
                  rowName="Free Cash Flow"
                  onHover={handleCellHover}
                  onLeave={handleCellLeave}
                />
              );
            })}
          </tr>
        </tbody>
      </table>
    </div>
  );
};

// Render the ratios table
const renderRatios = () => {
  if (!financialData.ratios || !financialData.ratios.financialRatios || financialData.ratios.financialRatios.length === 0) {
    return <div>No financial ratio data available</div>;
  }
  
  const ratios = financialData.ratios.financialRatios.slice().sort((a, b) => new Date(b.date) - new Date(a.date));
  
  return (
    <div className="financial-table-container">
      <table className="financial-table">
        <thead>
          <tr>
            <th>Ratio</th>
            {ratios.map((ratio, index) => (
              <th key={index}>{formatDate(ratio.date)}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>P/E Ratio</td>
            {ratios.map((ratio, index) => {
              const previousValue = index < ratios.length - 1 ? ratios[index + 1].pe_ratio : null;
              return (
                <TableCell 
                  key={index}
                  value={ratio.pe_ratio}
                  previousValue={previousValue}
                  formatter={(v) => safeToFixed(v)}
                  rowName="P/E Ratio"
                  onHover={handleCellHover}
                  onLeave={handleCellLeave}
                />
              );
            })}
          </tr>
          <tr>
            <td>Price to Sales</td>
            {ratios.map((ratio, index) => {
              const previousValue = index < ratios.length - 1 ? ratios[index + 1].price_to_sales : null;
              return (
                <TableCell 
                  key={index}
                  value={ratio.price_to_sales}
                  previousValue={previousValue}
                  formatter={(v) => safeToFixed(v)}
                  rowName="Price to Sales"
                  onHover={handleCellHover}
                  onLeave={handleCellLeave}
                />
              );
            })}
          </tr>
          <tr>
            <td>Price to Book</td>
            {ratios.map((ratio, index) => {
              const previousValue = index < ratios.length - 1 ? ratios[index + 1].price_to_book : null;
              return (
                <TableCell 
                  key={index}
                  value={ratio.price_to_book}
                  previousValue={previousValue}
                  formatter={(v) => safeToFixed(v)}
                  rowName="Price to Book"
                  onHover={handleCellHover}
                  onLeave={handleCellLeave}
                />
              );
            })}
          </tr>
          <tr>
            <td>Debt to Equity</td>
            {ratios.map((ratio, index) => {
              const previousValue = index < ratios.length - 1 ? ratios[index + 1].debt_to_equity : null;
              return (
                <TableCell 
                  key={index}
                  value={ratio.debt_to_equity}
                  previousValue={previousValue}
                  formatter={(v) => safeToFixed(v)}
                  rowName="Debt to Equity"
                  onHover={handleCellHover}
                  onLeave={handleCellLeave}
                />
              );
            })}
          </tr>
          <tr>
            <td>Current Ratio</td>
            {ratios.map((ratio, index) => {
              const previousValue = index < ratios.length - 1 ? ratios[index + 1].current_ratio : null;
              return (
                <TableCell 
                  key={index}
                  value={ratio.current_ratio}
                  previousValue={previousValue}
                  formatter={(v) => safeToFixed(v)}
                  rowName="Current Ratio"
                  onHover={handleCellHover}
                  onLeave={handleCellLeave}
                />
              );
            })}
          </tr>
          <tr>
            <td>Return on Assets</td>
            {ratios.map((ratio, index) => {
              const previousValue = index < ratios.length - 1 ? ratios[index + 1].return_on_assets : null;
              return (
                <TableCell 
                  key={index}
                  value={ratio.return_on_assets}
                  previousValue={previousValue}
                  formatter={(roa) => (ensureNumber(roa) * 100).toFixed(2) + '%'}
                  rowName="Return on Assets"
                  onHover={handleCellHover}
                  onLeave={handleCellLeave}
                />
              );
            })}
          </tr>
          <tr>
            <td>Return on Equity</td>
            {ratios.map((ratio, index) => {
              const previousValue = index < ratios.length - 1 ? ratios[index + 1].return_on_equity : null;
              return (
                <TableCell 
                  key={index}
                  value={ratio.return_on_equity}
                  previousValue={previousValue}
                  formatter={(roe) => (ensureNumber(roe) * 100).toFixed(2) + '%'}
                  rowName="Return on Equity"
                  onHover={handleCellHover}
                  onLeave={handleCellLeave}
                />
              );
            })}
          </tr>
          <tr>
            <td>Gross Margin</td>
            {ratios.map((ratio, index) => {
              const previousValue = index < ratios.length - 1 ? ratios[index + 1].gross_margin : null;
              return (
                <TableCell 
                  key={index}
                  value={ratio.gross_margin}
                  previousValue={previousValue}
                  formatter={(gm) => (ensureNumber(gm) * 100).toFixed(2) + '%'}
                  rowName="Gross Margin"
                  onHover={handleCellHover}
                  onLeave={handleCellLeave}
                />
              );
            })}
          </tr>
          <tr>
            <td>Operating Margin</td>
            {ratios.map((ratio, index) => {
              const previousValue = index < ratios.length - 1 ? ratios[index + 1].operating_margin : null;
              return (
                <TableCell 
                  key={index}
                  value={ratio.operating_margin}
                  previousValue={previousValue}
                  formatter={(om) => (ensureNumber(om) * 100).toFixed(2) + '%'}
                  rowName="Operating Margin"
                  onHover={handleCellHover}
                  onLeave={handleCellLeave}
                />
              );
            })}
          </tr>
          <tr>
            <td>Net Profit Margin</td>
            {ratios.map((ratio, index) => {
              const previousValue = index < ratios.length - 1 ? ratios[index + 1].net_profit_margin : null;
              return (
                <TableCell 
                  key={index}
                  value={ratio.net_profit_margin}
                  previousValue={previousValue}
                  formatter={(npm) => (ensureNumber(npm) * 100).toFixed(2) + '%'}
                  rowName="Net Profit Margin"
                  onHover={handleCellHover}
                  onLeave={handleCellLeave}
                />
              );
            })}
          </tr>
          <tr>
            <td>Dividend Yield</td>
            {ratios.map((ratio, index) => {
              const previousValue = index < ratios.length - 1 ? ratios[index + 1].dividend_yield : null;
              return (
                <TableCell 
                  key={index}
                  value={ratio.dividend_yield}
                  previousValue={previousValue}
                  formatter={(dy) => (ensureNumber(dy) * 100).toFixed(2) + '%'}
                  rowName="Dividend Yield"
                  onHover={handleCellHover}
                  onLeave={handleCellLeave}
                />
              );
            })}
          </tr>
        </tbody>
      </table>
    </div>
  );
};
  
  // Similar implementations for renderCashFlows() and renderRatios()
  // Follow the same pattern of using TableCell components
  
  return (
    <div className="financial-data">
      <div className="period-selector">
        <label>Period:</label>
        <select 
          value={period} 
          onChange={(e) => {
            console.log(`Changing period to ${e.target.value}`);
            setPeriod(e.target.value);
          }}
        >
          <option value="annual">Annual</option>
          <option value="quarter">Quarterly</option>
        </select>
      </div>
      
      <div className="financial-tabs">
        <button 
          className={activeFinancialTab === 'income' ? 'active' : ''} 
          onClick={() => setActiveFinancialTab('income')}
        >
          Income Statement
        </button>
        <button 
          className={activeFinancialTab === 'balance' ? 'active' : ''} 
          onClick={() => setActiveFinancialTab('balance')}
        >
          Balance Sheet
        </button>
        <button 
          className={activeFinancialTab === 'cashflow' ? 'active' : ''} 
          onClick={() => setActiveFinancialTab('cashflow')}
        >
          Cash Flow
        </button>
        <button 
          className={activeFinancialTab === 'ratios' ? 'active' : ''} 
          onClick={() => setActiveFinancialTab('ratios')}
        >
          Ratios
        </button>
      </div>
      
      <div className="financial-content">
        {activeFinancialTab === 'income' && renderIncomeStatements()}
        {activeFinancialTab === 'balance' && renderBalanceSheets()}
        {activeFinancialTab === 'cashflow' && renderCashFlows()}
        {activeFinancialTab === 'ratios' && renderRatios()}
      </div>
      
      {/* Hover info tooltip */}
      <HoverInfo position={hoverInfo.position} content={hoverInfo.content} />
    </div>
  );
};

export default FinancialData;